import type { PopoverProps } from "@mui/material";
import { Box, Button, Popover } from "@mui/material";
import { ColorPicker } from "mui-tiptap";
import type { MouseEvent } from "react";
import { useState } from "react";
import { getColorBrightness, isValidHexColor } from "@entrilia/shared/utilities/colorHelper";

const BUTTON_SIZE = 24;

// MUI's `rgbToHex` behind the default `colorToHex` returns any "#"-prefixed string as-is, so "#" would pass as a color.
const getValidHexColor = (color: string): string | null => {
  const hexColor = color.startsWith("#") ? color : `#${color}`;
  return isValidHexColor(hexColor) ? hexColor : null;
};

interface Props {
  color: string;
  popoverProps: Partial<PopoverProps>;
  disabled?: boolean;
  onColorChange: (newColor: string) => void;
}

const ColorEdit = ({ color, onColorChange, popoverProps, disabled }: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleButtonClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (disabled) {
      return;
    }
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);
  const handleColorChange = (newColor: string) => onColorChange(newColor.toUpperCase());

  const colorValue = color.toUpperCase();
  const showBorder = getColorBrightness(color) > 0.9;

  return (
    <>
      <Button
        variant="contained"
        onClick={handleButtonClick}
        sx={{
          width: BUTTON_SIZE,
          height: BUTTON_SIZE,
          p: 0,
          minWidth: 0,
          backgroundColor: color,
          "&:hover": { backgroundColor: color },
          border: showBorder ? "0.5px solid" : undefined,
          borderColor: "divider",
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      />
      <Popover
        {...popoverProps}
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        slotProps={{ paper: { sx: { m: 1 } } }}
      >
        {/* Same width as mui-tiptap's own ColorPickerPopper, so both pickers look alike. */}
        <Box sx={{ p: 2, width: 235 }}>
          <ColorPicker value={colorValue} onChange={handleColorChange} colorToHex={getValidHexColor} disableAlpha />
        </Box>
      </Popover>
    </>
  );
};

export default ColorEdit;
